"use client";

import { useState, useRef, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  COUNTRY_CODES,
  ease,
  type CountryCode
} from "@/components/forms/constants";
import { TextField } from "./TextField";
import { ChevronDownIcon } from "./Icons/ChevronDownIcon";

// ─── FloatMenu ────────────────────────────────────────────────────────────────

function FloatMenu({
  anchor,
  onClose,
  children
}: {
  anchor: DOMRect;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const style: CSSProperties = {
    position: "fixed",
    top: anchor.bottom + 4,
    left: anchor.left,
    width: Math.max(anchor.width, 260),
    zIndex: 200,
    background: "var(--color-bg-elevated)",
    border: "1px solid var(--color-border)",
    borderRadius: "12px",
    padding: "4px",
    boxShadow: "var(--shadow-float)"
  };

  return createPortal(
    <>
      <div
        style={{ position: "fixed", inset: 0, zIndex: 199 }}
        onMouseDown={(e) => {
          e.preventDefault();
          onClose();
        }}
      />
      <motion.div
        style={style}
        initial={{ opacity: 0, y: -6, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -6, scale: 0.98 }}
        transition={{ duration: 0.15, ease }}
      >
        {children}
      </motion.div>
    </>,
    document.body
  );
}

// ─── CountryCodeDropdown ──────────────────────────────────────────────────────

function CountryCodeDropdown({
  value,
  onChange
}: {
  value: { code: string; iso: string };
  onChange: (code: string, iso: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  const cc =
    COUNTRY_CODES.find((c) => c.iso === value.iso && c.code === value.code) ??
    COUNTRY_CODES.find((c) => c.code === value.code) ??
    COUNTRY_CODES[0];

  const filtered = query
    ? COUNTRY_CODES.filter(
        (c) =>
          c.label.toLowerCase().includes(query.toLowerCase()) ||
          c.code.includes(query)
      )
    : COUNTRY_CODES;

  const toggle = () => {
    if (open) {
      setOpen(false);
      return;
    }
    setAnchor(btnRef.current?.getBoundingClientRect() ?? null);
    setOpen(true);
  };

  const select = (c: CountryCode) => {
    if (c.blocked) return;
    onChange(c.code, c.iso);
    setOpen(false);
    setQuery("");
  };

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        className={`flex items-center gap-1.5 bg-transparent outline-none pt-1 pr-4 text-base font-body text-text-primary whitespace-nowrap transition-colors duration-200 ${
          open
            ? "border-b-2 border-bubblegum pb-[7px]"
            : "border-b border-border pb-2"
        }`}
      >
        <span>{cc.flag}</span>
        <span>{cc.code}</span>
        <ChevronDownIcon
          className={`w-3 h-3 text-text-muted transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      <AnimatePresence>
        {open && anchor && (
          <FloatMenu anchor={anchor} onClose={() => setOpen(false)}>
            <div className="p-1.5 pb-0">
              <input
                autoFocus
                type="text"
                aria-label="Search country"
                placeholder="Search country…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full bg-bg-base border border-border rounded-md px-2.5 py-2 text-sm font-body text-text-primary placeholder:text-text-muted outline-none focus:border-bubblegum transition-colors"
              />
            </div>
            <div className="h-px bg-border mx-1.5 my-1.5" />
            <div
              className="overflow-y-auto scrollbar-thin"
              style={{ maxHeight: 220 }}
            >
              {filtered.map((c, i) => (
                <div key={`${c.iso}-${i}`}>
                  {!query && !c.focus && i > 0 && filtered[i - 1]?.focus && (
                    <div className="h-px bg-border mx-2 my-1" />
                  )}
                  <button
                    type="button"
                    disabled={c.blocked}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      select(c);
                    }}
                    className="flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-sm font-body text-text-body hover:text-text-primary tint-hover-weak transition-colors disabled:opacity-35 disabled:cursor-not-allowed"
                  >
                    <span className="text-base">{c.flag}</span>
                    <span className="w-10 text-text-primary">{c.code}</span>
                    <span className="text-text-muted flex-1 text-left">
                      {c.label}
                    </span>
                    {c.blocked && (
                      <span className="text-xs text-text-muted">
                        Unavailable
                      </span>
                    )}
                  </button>
                </div>
              ))}
            </div>
          </FloatMenu>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── PhoneField ───────────────────────────────────────────────────────────────

export interface PhoneFieldProps {
  label: string;
  countryCode: string;
  countryIso: string;
  onCountryChange: (code: string, iso: string) => void;
  phone: string;
  onPhoneChange: (value: string) => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  placeholder?: string;
  error?: string;
  autoFocus?: boolean;
}

export function PhoneField({
  label,
  countryCode,
  countryIso,
  onCountryChange,
  phone,
  onPhoneChange,
  onKeyDown,
  onBlur,
  placeholder = "70 123 45 67",
  error,
  autoFocus
}: PhoneFieldProps) {
  return (
    <div className="space-y-2">
      {label && (
        <label className="text-sm text-text-body font-body">{label}</label>
      )}
      <div className="flex items-end">
        <CountryCodeDropdown
          value={{ code: countryCode, iso: countryIso }}
          onChange={onCountryChange}
        />
        <TextField
          label=""
          hideErrorMessage
          error={error}
          type="tel"
          inputMode="numeric"
          value={phone}
          onChange={(e) =>
            onPhoneChange(e.target.value.replace(/[^0-9 \-]/g, ""))
          }
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          placeholder={placeholder}
          autoComplete="tel-national"
          autoFocus={autoFocus}
          className="flex-1"
        />
      </div>
      {error && <p className="text-xs text-error font-body mt-1.5">{error}</p>}
    </div>
  );
}
